import { create } from 'zustand';
import type { AgentAvailability, AgentDirectoryEntryDto, AgentInfo } from '@/types';

export interface AgentState {
  /** Current agent's availability status. */
  myAvailability: AgentAvailability;
  /** Other online agents in the tenant. */
  onlineAgents: AgentInfo[];

  /**
   * agentId → displayName for every ACTIVE staff user in the tenant, fetched once at shell boot.
   *
   * Distinct from {@link onlineAgents}, which is PRESENCE: a conversation's owner is very often
   * someone who is currently offline, so resolving owner names from the presence list would leave
   * most of them unresolved. Empty until the fetch lands (and stays empty if it fails), which
   * callers must treat as "name unknown" rather than "no owner".
   */
  directory: Record<string, string>;

  /** Set the current agent's availability. */
  setMyAvailability: (availability: AgentAvailability) => void;

  /** Replace the full list of online agents. */
  setOnlineAgents: (agents: AgentInfo[]) => void;

  /** Replace the id→name directory from the agent-directory endpoint. */
  setDirectory: (entries: AgentDirectoryEntryDto[]) => void;

  /** Add an agent that just joined. */
  addAgent: (agentId: string) => void;

  /** Remove an agent that disconnected. */
  removeAgent: (agentId: string) => void;
}

export const useAgentStore = create<AgentState>((set) => ({
  myAvailability: 'OFFLINE',
  onlineAgents: [],
  directory: {},

  setMyAvailability: (availability) => {
    set({ myAvailability: availability });
  },

  setOnlineAgents: (agents) => {
    set({ onlineAgents: agents });
  },

  setDirectory: (entries) => {
    // Blank display names are dropped rather than stored: an empty string would resolve to a blank
    // owner cell, which reads as "no owner" when the truth is "assigned, name unknown".
    const next: Record<string, string> = {};
    for (const e of entries) {
      const name = (e?.displayName ?? '').trim();
      if (e?.agentId && name) next[e.agentId] = name;
    }
    set({ directory: next });
  },

  addAgent: (agentId) => {
    set((state) => {
      if (state.onlineAgents.some((a) => a.agentId === agentId)) {
        return state;
      }
      return {
        onlineAgents: [
          ...state.onlineAgents,
          {
            agentId,
            displayName: agentId,
            availability: 'ONLINE' as AgentAvailability,
            connectedAt: new Date().toISOString(),
          },
        ],
      };
    });
  },

  removeAgent: (agentId) => {
    set((state) => ({
      onlineAgents: state.onlineAgents.filter((a) => a.agentId !== agentId),
    }));
  },
}));
